import { Box, ThemeProvider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import ChatInterface from '../components/chatInterface';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';

import { ACTIONS } from 'constant';
import { FEATURE } from 'constant/features';
import { SERVICE_PROVIDER_ROLES } from 'data/constants/roles';
import useClientViewChat from 'hooks/useClientViewChat';
import useHasFeature from 'hooks/useHasFeature';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { ChatMessage, ChatReference } from 'types';

interface ChatLocationState {
    references?: ChatReference[];
}

interface ClientViewChatProps {
    enableReplySuggestions?: boolean;
}

const ClientViewChatInner: FC<ClientViewChatProps> = ({ enableReplySuggestions = false }) => {
    const location = useLocation();
    const { id: candidateIdParam } = useParams();
    const { translate } = useTranslate();
    const references = (location.state as ChatLocationState | null)?.references;
    const { id: userId, role } = useSelector((state: RootState) => state.user.entity);
    const hasReplySuggestions = useHasFeature(FEATURE.REPLY_SUGGESTIONS);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const parsedCandidateId = Number(candidateIdParam);
    const candidateId =
        Number.isInteger(parsedCandidateId) && parsedCandidateId > 0 ? parsedCandidateId : null;
    const [isBackOffice, setIsBackOffice] = useState(!references?.length);
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<ChatMessage>();
    const {
        isLoading,
        messages,
        users,
        hasAccess,
        canAccessBackoffice,
        sendingMessage,
        isSuggestingReply,
        canRetryReplySuggestion,
        getApplicationChat,
        handleSendMessage,
        handleSuggestReply,
        handleDeleteMessage,
        resetChat,
    } = useClientViewChat();

    const hasBackofficeAssigned = Boolean(activeApp?.backoffice_id && activeApp?.backoffice_name);
    const activeApplicationMatchesProfile =
        !enableReplySuggestions ||
        (candidateId !== null && activeApp?.candidate_id === candidateId);
    const hasActiveApplication = Boolean(activeApp?.id && activeApplicationMatchesProfile);
    const canSuggestReply =
        enableReplySuggestions &&
        hasReplySuggestions &&
        !isBackOffice &&
        hasActiveApplication &&
        candidateId !== null &&
        SERVICE_PROVIDER_ROLES.includes(role);
    const chatRootRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number>();

    useEffect(() => {
        const node = chatRootRef.current;
        if (!node) {
            return;
        }

        const updateHeight = () => {
            const top = node.getBoundingClientRect().top;
            const bottomGap = window.innerWidth < 769 ? 50 : 8;
            setContainerHeight(Math.max(240, Math.floor(window.innerHeight - top - bottomGap)));
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    useEffect(() => {
        if (!userId) {
            return;
        }

        if (!activeApp?.id || !activeApplicationMatchesProfile) {
            resetChat();
            return;
        }

        void getApplicationChat(activeApp.id, Boolean(hasBackofficeAssigned && isBackOffice));
    }, [
        activeApp,
        activeApplicationMatchesProfile,
        getApplicationChat,
        hasBackofficeAssigned,
        isBackOffice,
        resetChat,
        userId,
    ]);

    useEffect(() => {
        if (isBackOffice && (!hasBackofficeAssigned || !canAccessBackoffice)) {
            setIsBackOffice(false);
        }
    }, [canAccessBackoffice, hasBackofficeAssigned, isBackOffice]);

    const handleAction = (action: ACTIONS, message?: ChatMessage) => {
        if (action !== ACTIONS.DELETE || !message) {
            return;
        }

        setModalObject((prev) => ({
            ...prev,
            action,
            title: translate('deleteMessage'),
            content: translate('areYouSureDeleteMessage'),
            data: message,
            disabled: false,
            loading: false,
        }));
    };

    const handleCloseModal = () => {
        if (modalObject.loading) {
            return;
        }

        onResetModalObj();
    };

    const handleConfirmDelete = async () => {
        const message = modalObject.data;
        if (!message) {
            return;
        }

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));

        try {
            await handleDeleteMessage(message.id);
            onResetModalObj();
        } catch {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
        }
    };

    const requestReplySuggestion = () => {
        if (!canSuggestReply || candidateId === null || !activeApp?.id) {
            return Promise.resolve(null);
        }

        return handleSuggestReply(candidateId, activeApp.id);
    };

    return (
        <Box
            ref={chatRootRef}
            sx={{
                width: '100%',
                px: { xs: 1.5, md: 0 },
                height: containerHeight,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <ChatInterface
                users={users}
                messages={messages}
                isLoading={isLoading}
                isSending={sendingMessage}
                isSuggestingReply={isSuggestingReply}
                hasAccess={hasAccess}
                isBackOffice={isBackOffice}
                canAccessBackoffice={canAccessBackoffice}
                canRetryReplySuggestion={canRetryReplySuggestion}
                canSuggestReply={canSuggestReply}
                hasBackofficeAssigned={hasBackofficeAssigned}
                hasActiveApplication={hasActiveApplication}
                references={references}
                onToggle={() => setIsBackOffice((prev) => !prev)}
                onSend={handleSendMessage}
                onSuggestReply={requestReplySuggestion}
                onDelete={(message) => handleAction(ACTIONS.DELETE, message)}
            />

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                />
            ) : null}
        </Box>
    );
};

const ClientViewChat: FC<ClientViewChatProps> = (props) => (
    <ThemeProvider theme={appThemeV2}>
        <ClientViewChatInner {...props} />
    </ThemeProvider>
);

export default ClientViewChat;
